import React from 'react';

import { BackLink, Breadcrumbs, MarketShell } from '../../design-system/components/shells';
import { Button, FilterRail, PageHeader, Pagination } from '../../design-system/components/desktop';
import { Cta, Sheet, SheetBody, SheetHeader } from '../../design-system/components/mobile';
import { SrOnly } from '../../design-system/components/desktop/primitives/SrOnly';
import { useViewport } from '../../design-system/hooks/useViewport';
import { routes } from '../../routes/routes';

import MarketplaceHeader from './components/MarketplaceHeader/MarketplaceHeader';
import MarketplaceFilters from './components/MarketplaceFilters/MarketplaceFilters';
import MarketplaceFilterSheet from './components/MarketplaceFilters/MarketplaceFilterSheet';
import { countAppliedFilters } from './components/MarketplaceFilters/filterModel';
import MarketplaceGrid from './components/MarketplaceGrid/MarketplaceGrid';
import MarketplaceSorting from './components/MarketplaceSorting/MarketplaceSorting';

/**
 * Marketplace, the view. Pure: every input is a prop, every output a callback.
 *
 * The container (`Marketplace.jsx`) owns the query, the fetch race guard, the
 * seller self-listing filter, the URL parameters and the page state. This file
 * owns what a buyer sees, which is why the state explorer can mount it with
 * fixture props and get the real screen back rather than a mock of it.
 *
 * THE ONE THING THAT BRANCHES, AND WHY
 * ------------------------------------
 * The filter surface. Above 1024 it is a sticky 256px `FilterRail` sitting in
 * the page's own grid; below it, it is a bottom `Sheet`. That is one of the
 * rows in the mobile-to-desktop map, and the two are not the same DOM with
 * different padding: the rail is always present and part of the layout, the
 * sheet is a modal layer with a focus trap, a scrim and a footer. Rendering
 * both and hiding one would double the DOM, put a whole second facet panel in
 * the tab order, and ship two sets of change handlers for one job.
 *
 * They are not the same COMPONENT either, and that is the same argument one
 * level down. The rail expands every facet at once, which is what a column
 * that never leaves the screen is for. The sheet is `MarketplaceFilterSheet`:
 * drill rows into sliding sub-panels, a searchable set list, a switch and a
 * segmented control — the shape the mobile library was drawn in. Both read the
 * same filter object and call the same `onFilterChange`.
 *
 * Everything else, the header, the grid, the cards and the pagination, is one
 * tree on both tracks. Each of those components either emits identical markup
 * or makes its own track choice internally.
 *
 * WHAT WENT AWAY
 * --------------
 * - 98 lines of hand-rolled pagination, byte-identical to the copy in
 *   `SellerStore.jsx`. Both are now the shared `Pagination`, which brings the
 *   result summary, `aria-current="page"` on the current page, and real
 *   disabled attributes on the ends.
 * - `FilterModal`, a bespoke dialog with its own focus trap and scroll lock,
 *   used here only to fake a bottom sheet. The mobile track already has a
 *   `Sheet` that does all of it properly.
 * - `Layout type="header"` from `SellerDashboard/`, a seller-console chrome
 *   wrapped around a buying page. The buying shell is `MarketShell`.
 * - `Marketplace.css` in full, and the `isMobile` state driven by a fourth
 *   competing breakpoint at 480px. The only threshold on this page is now
 *   `useViewport()`'s 1024.
 */
export function MarketplaceView({
  cards = [],
  loading = false,
  error,
  onRetry,
  totalCards = 0,
  page = 1,
  totalPages = 1,
  pageSize = 15,
  onPageChange,

  filters,
  onFilterChange,
  /* THE RESET, and it is not `onFilterChange(CLEARED_FILTERS)`.
     `isFiltered` below counts the SEARCH TERM as well as the facets, so a query
     that excludes everything raises the filtered empty state with no facets
     applied -- and clearing filters that were already clear did nothing at all,
     leaving the buyer on an empty page whose only way out was to find and empty
     the search box by hand. The page owns what "everything" means (facets,
     query and page number all live in different places), so it hands one
     handler down and every clear control calls it. */
  onClearAll,

  /* How many cards the query matched once ONLY the availability default was
     dropped, and the handler that drops it. Above zero means the results are
     empty for one reason and one reason only: nobody has listed any of them.
     See the container, which is the only place that count can be known. */
  catalogueMatches = 0,
  onIncludeUnlisted,

  sortBy,
  onSortChange,
  sortOptions = [],

  searchTerm,
  onSearchChange,

  viewMode = 'grid',
  onViewModeChange,

  onCardClick,

  filtersOpen = false,
  onOpenFilters,
  onCloseFilters,
  sortOpen = false,
  onOpenSort,
  onCloseSort,

  variant = 'browse',
  query = '',

  savedIds,
  onToggleSaved,

  /* An explicit way back, the same pair `catalog.view.jsx` takes. Browse is a
     top-level destination and its crumb says so — but a buyer who arrived by
     drilling /sets -> a game -> a set is three levels deep with nothing on
     screen admitting it, on a page that does not even name the set. The crumb
     says where this page LIVES; only the caller knows where the buyer WAS. */
  backTo,
  backToLabel,
}) {
  const { isDesktop } = useViewport();
  const appliedCount = countAppliedFilters(filters);
  const isFiltered = appliedCount > 0 || Boolean(searchTerm);

  /* The label tells the truth about what the button clears. With a query in
     play the reset takes the search with it, and "Clear all filters" would be
     a promise the click overshoots. */
  const clearLabel = searchTerm ? 'Clear search and filters' : 'Clear all filters';

  /* THE SEARCH TRAP. The nav typeahead searches the whole catalogue, so a
     query can offer six cards and then land here on nothing, because this page
     ships `available_to_buy` on. Reporting that as "Nothing matches those
     filters" was wrong twice over: the buyer applied no filters, and their
     query matched plenty. When the container has counted real catalogue
     matches, the page says so and offers the door that keeps the query. */
  const unlistedOnly = catalogueMatches > 0 && Boolean(onIncludeUnlisted);
  /* `en-ZA`, like every other COUNT in the app — the result bar, the page
     header subtitle, the filter sheet's apply button and `Rating`. Money is the
     one that uses `en-US`, via `formatCurrency`. Getting this wrong put
     "2,344" here directly above a result bar reading "2 344". */
  const unlistedCount = Number(catalogueMatches).toLocaleString('en-ZA');

  const isSearch = variant === 'search';
  const title = isSearch ? 'Search results' : 'Marketplace';
  const crumbs = isSearch
    ? [
        { label: 'Home', to: routes.HOME },
        { label: 'Marketplace', to: routes.MARKETPLACE },
        { label: 'Search' },
      ]
    : [{ label: 'Home', to: routes.HOME }, { label: 'Marketplace' }];

  const header = (
    <MarketplaceHeader
      title={title}
      /* Desktop puts the H1 and the count in the rail, over the filters, so
         the band above the grid is controls only. Below the handoff there is
         no rail and `ResultBar` carries the count as it always did. */
      showTitle={false}
      sortBy={sortBy}
      onSortChange={onSortChange}
      sortOptions={sortOptions}
      viewMode={viewMode}
      onViewModeChange={onViewModeChange}
      onOpenFilters={onOpenFilters}
      onOpenSort={onOpenSort}
      appliedCount={appliedCount}
      totalCards={loading ? null : totalCards}
      /* The query, for the mobile bar. Desktop prints it in the PageHeader
         subtitle above; below the handoff nothing on the screen said what was
         searched for — the top bar reads "Search" from the crumb, there is no
         visible URL mid-scroll, and the bar's whole text was "12 cards". */
      query={isSearch ? query : undefined}
    />
  );

  const results = (
    <>
      <MarketplaceGrid
        cards={cards}
        viewMode={viewMode}
        loading={loading}
        error={error}
        onRetry={onRetry}
        onCardClick={onCardClick}
        cardHref={(card) => `${routes.MARKETPLACE}/card/${card.id}`}
        savedIds={savedIds}
        onToggleSaved={onToggleSaved}
        /* `market_price` here is `Card.cached_price_zar`, a catalog reference
           price, NOT the cheapest listing — the store page means something
           else by the same field. Browse has no listing minimum to show, so
           the tile labels the figure instead of printing it as a buy price. */
        priceBasis="market"
        /* `available` here is Count(listings) for the card, NOT a quantity.
           The store page means something else by the same field.
           At zero the tile says NOTHING: a card with no listings is not a
           product that sold out, it is a catalog entry nobody has listed yet,
           and stamping "Sold out" on it invented a state the shop never had. */
        availabilityLabel={(available) =>
          available > 0
            ? `${available} ${available === 1 ? 'listing' : 'listings'}`
            : undefined
        }
        emptyTone={isFiltered ? 'filtered' : 'default'}
        emptyTitle={
          unlistedOnly
            ? 'No one is selling that yet'
            : isFiltered
              ? 'Nothing matches those filters'
              : 'No cards yet'
        }
        emptyText={
          unlistedOnly
            ? `${unlistedCount} ${catalogueMatches === 1 ? 'card matches' : 'cards match'} in the catalogue, but none of them has a listing right now.`
            : isFiltered
              ? searchTerm
                ? 'No cards match '
                : 'Every card is excluded by the filters you have applied. Widen one and they come back.'
              : 'Cards appear here as sellers list them.'
        }
        emptyQuery={!unlistedOnly && isFiltered && searchTerm ? searchTerm : undefined}
        emptyActions={
          unlistedOnly ? (
            /* Both doors, strongest first. Seeing the cards is what the buyer
               came for — a card page still carries the market price, the price
               history and the heart, so an unlisted card is a real destination
               and not a consolation. Clearing stays available and stays quiet. */
            <>
              {isDesktop ? (
                <Button onClick={onIncludeUnlisted}>
                  Show all {unlistedCount} catalogue {catalogueMatches === 1 ? 'match' : 'matches'}
                </Button>
              ) : (
                <Cta size="sm" onClick={onIncludeUnlisted}>
                  Show all {unlistedCount} catalogue {catalogueMatches === 1 ? 'match' : 'matches'}
                </Cta>
              )}
              {isDesktop ? (
                <Button variant="ghost" onClick={onClearAll}>
                  {clearLabel}
                </Button>
              ) : (
                <Cta variant="ghost" size="sm" onClick={onClearAll}>
                  {clearLabel}
                </Cta>
              )}
            </>
          ) : isFiltered ? (
            isDesktop ? (
              <Button variant="ghost" onClick={onClearAll}>
                {clearLabel}
              </Button>
            ) : (
              <Cta variant="ghost" size="sm" onClick={onClearAll}>
                {clearLabel}
              </Cta>
            )
          ) : null
        }
      />

      {/* Pagination hides while results are loading, as it always did: a page
          run built from a stale total sends the buyer to a page that no longer
          exists. It also hides at a single page, because one page of results
          has no navigation to offer. */}
      {!loading && !error && totalPages > 1 && (
        <Pagination
          /* Same story as the toolbar: vendored as table chrome, mounted here
             as a page band. `.dsa-browse-pager` in app-layer.css drops the
             24px inset and gives the grid air above the rule. */
          className="dsa-browse-pager"
          page={page}
          pageCount={totalPages}
          onPageChange={onPageChange}
          totalItems={totalCards}
          pageSize={pageSize}
          itemNoun="cards"
        />
      )}
    </>
  );

  return (
    <MarketShell
      activeNav="marketplace"
      /* No `breadcrumbs` prop on desktop, ON PURPOSE. The shell renders the
         trail full width directly under the nav; here it belongs in the left
         column with the title and the filters, so this page renders its own
         `Breadcrumbs` inside the rail. Below the handoff the shell needs the
         trail to title the top bar, so it still gets it there. */
      breadcrumbs={isDesktop ? [] : crumbs}
      back={backTo ? { to: backTo, label: `Back to ${backToLabel || 'where you were'}` } : undefined}
      /* Browse is the one surface that earns the whole viewport: the rail eats
         256px of it and every pixel left buys another column of card art. */
      contentClassName={isDesktop ? 'dsa-page--wide' : undefined}
      /* No `search` prop. The nav box is a GLOBAL search now, wired once in
         `MarketShell` via `useGlobalSearch`: typing opens a typeahead, Enter
         goes to `/search?q=`. This page used to own that box and filter its
         own grid with it live, which is why it also had to suppress the
         typeahead — a dropdown of card names sat on top of a grid already
         showing those cards. Splitting browse from search retires both
         problems. */
    >
      {/* THE HEADING THE MOBILE LAYOUT DOES NOT DRAW.
          Below the handoff this page renders a `ResultBar`, not a
          `PageHeader`: the visible H1 lives in the desktop filter rail, and
          the mobile top bar shows the wordmark instead because the tab bar
          underneath already names the surface. Both are deliberate looks — but
          the consequence was that `/marketplace` and `/search` were the only
          two mobile routes in the app with no `<h1>` at all, so the document
          went straight from nothing to the `<h3>` on each card tile and
          heading navigation had no entry point.

          Read, not seen. The visible design is untouched, and desktop still
          has exactly one H1: the real one, below. */}
      {!isDesktop && <SrOnly as="h1">{title}</SrOnly>}

      {isDesktop ? (
        <>
          {/* THE TITLE BELONGS TO THE PAGE, NOT TO THE FILTERS. It briefly
              lived in the rail, stacked above the facets, which put the name
              of the whole page inside the column that narrows one part of it —
              so "Marketplace" read as a heading for the filter list. It is a
              full-width page header again, over both columns; only the facets
              are docked left. */}
          <div className="dsa-browse__head">
            {/* The back link belongs BESIDE the crumbs, and it has to be drawn
                here rather than by the shell: this page hands the shell no
                breadcrumbs on desktop (it draws them in the rail), and the
                shell's trail block — back link included — only renders when it
                has a trail of its own. Same control, same class, one
                implementation. */}
            {backTo ? (
              <div className="dsa-shell__trail">
                <BackLink back={{ to: backTo, label: `Back to ${backToLabel || 'where you were'}` }} />
                <Breadcrumbs items={crumbs} />
              </div>
            ) : (
              <Breadcrumbs items={crumbs} />
            )}
            <PageHeader
              title={title}
              /* On a results page the SUBTITLE is what was searched, not just
                 how many landed: a count with no query above it is a number
                 about nothing. */
              sub={
                loading
                  ? undefined
                  : isSearch
                    ? `${Number(totalCards).toLocaleString('en-ZA')} for “${query}”`
                    : `${Number(totalCards).toLocaleString('en-ZA')} cards`
              }
            />
          </div>

          <div className="dsa-browse">
            <FilterRail>
              <MarketplaceFilters
                filters={filters}
                onFilterChange={onFilterChange}
                onClearAll={onClearAll}
              />
            </FilterRail>
            <div>
              {header}
              {results}
            </div>
          </div>
        </>
      ) : (
        <>
          {header}
          {results}

          {/* The filter sheet owns its own `Sheet`, header and footer, because
              the drill panels have to slide the HEADER with them: a sub-panel
              titles itself and carries the back button, so a header rendered
              out here would sit above the stack saying "Filters" while the
              body showed a set list. See MarketplaceFilterSheet.jsx. */}
          <MarketplaceFilterSheet
            open={filtersOpen}
            onClose={onCloseFilters}
            filters={filters}
            onFilterChange={onFilterChange}
            onClearAll={onClearAll}
            totalCards={totalCards}
            loading={loading}
          />

          <Sheet open={sortOpen} onClose={onCloseSort}>
            <SheetHeader title="Sort by" />
            <SheetBody>
              <MarketplaceSorting
                sortOptions={sortOptions}
                value={sortBy}
                onSortChange={onSortChange}
                onClose={onCloseSort}
              />
            </SheetBody>
          </Sheet>
        </>
      )}
    </MarketShell>
  );
}

export default MarketplaceView;
